/**
 * Settings → Communication → API tokens (G20 public API / MCP auth).
 *
 * Vite SPA port of:
 *   frontend/src/app/(dashboard)/dashboard/settings/api-tokens/page.tsx
 *   frontend/src/hooks/use-api-tokens.ts
 *   frontend/src/lib/api/api-tokens.ts
 *
 * Self-service management of personal Bearer tokens that authenticate the
 * WorkRight MCP server AND the G20 public API (/api/v1/public/*). ANY role
 * (not just HR admins) can manage their own tokens — the backend enforces
 * (tenant_id, user_id) ownership so a token can only reach the creator's
 * tenant, and per-route role gates on the public API still apply (e.g. a
 * non-HR token is 403'd from /public/employees).
 *
 * UX contract:
 *   - Create: name + capability picker → server returns raw_token ONCE,
 *     shown in a one-time reveal dialog (never persisted to state after
 *     dismissal — the user must copy it).
 *   - List: name, capabilities, created/last-used/revoked timestamps.
 *     Active tokens first; revoked ones in a collapsed <details>.
 *   - Revoke: AlertDialog confirm → DELETE /{id}.
 *   - MCP connection instructions shown below the list.
 *
 * Migration note (Wave H, 2026-06-06): direct port following Vite SPA
 * conventions — TanStack Router createFileRoute, SettingsShell +
 * SettingsPageHeader wrapper, inline react-query hooks. NOT HR-gated
 * (tokens are user-scoped), so there is no useHrRoles() gate here.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Plus,
  Terminal,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { fmtDate, fmtDateTime } from "@/lib/leave-format";
import { toApiError } from "@/lib/api";
import {
  listApiTokens,
  createApiToken,
  revokeApiToken,
  type ApiToken,
  type CreateApiTokenRequest,
  type TokenCapability,
} from "@/lib/api/api-tokens";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/api-tokens",
)({
  component: SettingsApiTokensRoute,
});

// ---------------------------------------------------------------------------
// Query keys + hooks
// ---------------------------------------------------------------------------

const apiTokenKeys = {
  all: ["api-tokens"] as const,
  list: () => [...apiTokenKeys.all, "list"] as const,
};

function useApiTokens() {
  return useQuery({
    queryKey: apiTokenKeys.list(),
    queryFn: ({ signal }) => listApiTokens(signal),
    staleTime: 30 * 1000,
  });
}

function useCreateApiToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateApiTokenRequest) => createApiToken(body),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: apiTokenKeys.list() }),
  });
}

function useRevokeApiToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => revokeApiToken(id),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: apiTokenKeys.list() }),
  });
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CAP_OPTIONS: {
  value: TokenCapability;
  label: string;
  description: string;
}[] = [
  {
    value: "read",
    label: "Read",
    description:
      "GET endpoints only (employees, leave, expenses, accounting)",
  },
  {
    value: "write",
    label: "Write",
    description: "POST, PATCH, DELETE on entity-scoped resources",
  },
  {
    value: "admin",
    label: "Admin",
    description: "Tenant-level mutations (reserved for future use)",
  },
];

// ---------------------------------------------------------------------------
// CreateTokenDialog
// ---------------------------------------------------------------------------

function CreateTokenDialog({
  onCreated,
}: {
  readonly onCreated: (rawToken: string, name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [caps, setCaps] = useState<TokenCapability[]>(["read"]);
  const create = useCreateApiToken();

  function toggleCap(cap: TokenCapability) {
    setCaps((prev) =>
      prev.includes(cap) ? prev.filter((c) => c !== cap) : [...prev, cap],
    );
  }

  function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    const selectedCaps: TokenCapability[] =
      caps.length > 0 ? caps : ["read"];
    create.mutate(
      { name: name.trim(), capabilities: selectedCaps },
      {
        onSuccess: (res) => {
          setOpen(false);
          setName("");
          setCaps(["read"]);
          onCreated(res.raw_token, res.token.name);
        },
        onError: (err) => {
          toast.error(toApiError(err).message);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5" data-testid="api-token-add-btn">
          <Plus aria-hidden className="h-4 w-4" />
          New token
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create API token</DialogTitle>
            <DialogDescription>
              Give this token a label and choose what it can access. The token
              value will be shown once after creation.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="token-name">Token name</Label>
              <Input
                id="token-name"
                placeholder="e.g. Claude Desktop, Tally integration"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
                required
                autoFocus
                data-testid="api-token-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Capabilities</Label>
              <div className="space-y-2 rounded-md border p-3">
                {CAP_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex cursor-pointer items-start gap-3 text-sm"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
                      checked={caps.includes(opt.value)}
                      onChange={() => toggleCap(opt.value)}
                      data-testid={`api-token-cap-${opt.value}`}
                    />
                    <span>
                      <span className="font-medium">{opt.label}</span>
                      <span className="block text-xs text-muted-foreground">
                        {opt.description}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Select at least one capability. &quot;Write&quot; implies
                &quot;Read&quot;.
              </p>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || caps.length === 0 || create.isPending}
              data-testid="api-token-submit-btn"
            >
              {create.isPending && (
                <Loader2 aria-hidden className="mr-1.5 h-4 w-4 animate-spin" />
              )}
              Create token
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// RevealTokenDialog — one-time raw-token reveal
// ---------------------------------------------------------------------------

function RevealTokenDialog({
  rawToken,
  tokenName,
  onClose,
}: {
  readonly rawToken: string;
  readonly tokenName: string;
  readonly onClose: () => void;
}) {
  const [visible, setVisible] = useState(false);

  function copyToken() {
    void navigator.clipboard.writeText(rawToken);
    toast.success("Token copied to clipboard");
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound aria-hidden className="h-4 w-4 text-primary" />
            Token created: {tokenName}
          </DialogTitle>
          <DialogDescription>
            Copy this token now. It will not be shown again.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 rounded-md border bg-muted px-3 py-2">
          <div className="flex items-center gap-2">
            <code
              className="flex-1 break-all font-mono text-xs"
              data-testid="api-token-raw-value"
            >
              {visible ? rawToken : rawToken.replace(/./g, "•")}
            </code>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={() => setVisible((v) => !v)}
              aria-label={visible ? "Hide token" : "Show token"}
            >
              {visible ? (
                <EyeOff aria-hidden className="h-4 w-4" />
              ) : (
                <Eye aria-hidden className="h-4 w-4" />
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={copyToken}
              aria-label="Copy token"
            >
              <Copy aria-hidden className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Store this token in a password manager or your MCP client
          configuration. Once you close this dialog it cannot be recovered —
          you would need to revoke and create a new token.
        </p>
        <DialogFooter>
          <Button onClick={onClose}>Done — I have copied the token</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// TokenRow
// ---------------------------------------------------------------------------

function TokenRow({
  token,
  onRevoke,
  revoking,
}: {
  readonly token: ApiToken;
  readonly onRevoke: (id: number) => void;
  readonly revoking: boolean;
}) {
  const isRevoked = token.revoked_at != null;

  return (
    <div
      className="flex items-start justify-between gap-4 rounded-md border px-4 py-3"
      data-testid={`api-token-row-${token.id}`}
    >
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{token.name}</span>
          {isRevoked ? (
            <Badge variant="destructive" className="text-xs">
              Revoked
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs">
              Active
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
          <span>Created {fmtDate(token.created_at)}</span>
          {token.last_used_at && (
            <span>Last used {fmtDateTime(token.last_used_at)}</span>
          )}
          {token.revoked_at && <span>Revoked {fmtDate(token.revoked_at)}</span>}
        </div>
        <div className="flex flex-wrap gap-1 pt-0.5">
          {token.capabilities.map((cap) => (
            <Badge
              key={cap}
              variant="secondary"
              className="text-xs font-normal"
            >
              {cap}
            </Badge>
          ))}
        </div>
      </div>
      {!isRevoked && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              disabled={revoking}
              aria-label={`Revoke token "${token.name}"`}
              className="shrink-0 text-destructive hover:text-destructive"
              data-testid={`api-token-revoke-btn-${token.id}`}
            >
              {revoking ? (
                <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 aria-hidden className="h-4 w-4" />
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Revoke token?</AlertDialogTitle>
              <AlertDialogDescription>
                The token <strong>{token.name}</strong> will be permanently
                revoked. Any MCP server, integration, or agent using it will
                stop working immediately. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onRevoke(token.id)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Revoke token
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MCPInstructions
// ---------------------------------------------------------------------------

function MCPInstructions({
  hasActiveToken,
}: {
  readonly hasActiveToken: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Terminal aria-hidden className="h-4 w-4 text-primary" />
          Using the WorkRight MCP server
        </CardTitle>
        <CardDescription>
          Connect AI agents like Claude Desktop to your WorkRight data.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {!hasActiveToken && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            Create a token above to get started.
          </p>
        )}
        <div className="space-y-2">
          <p className="font-medium">1. Install the MCP server binary</p>
          <pre className="overflow-x-auto rounded-md bg-muted px-3 py-2 font-mono text-xs">
            go install
            github.com/nagavenkatesh/workright-app/backend/cmd/mcp-server@latest
          </pre>
        </div>
        <div className="space-y-2">
          <p className="font-medium">2. Add to Claude Desktop config</p>
          <p className="text-muted-foreground">
            Edit{" "}
            <code className="rounded bg-muted px-1">
              ~/Library/Application Support/Claude/claude_desktop_config.json
            </code>{" "}
            (macOS):
          </p>
          <pre className="overflow-x-auto rounded-md bg-muted px-3 py-2 font-mono text-xs">{`{
  "mcpServers": {
    "workright": {
      "command": "mcp-server",
      "env": {
        "WR_MCP_API_TOKEN": "<your-token>",
        "DATABASE_URL": "<your-postgres-url>"
      }
    }
  }
}`}</pre>
        </div>
        <div className="space-y-2">
          <p className="font-medium">3. Or call the public REST API directly</p>
          <p className="text-muted-foreground">
            Send the token as a Bearer header to any{" "}
            <code className="rounded bg-muted px-1">/api/v1/public/*</code>{" "}
            endpoint:
          </p>
          <pre className="overflow-x-auto rounded-md bg-muted px-3 py-2 font-mono text-xs">{`curl -H "Authorization: Bearer <your-token>" \\
  https://api.workright.in/api/v1/public/employees`}</pre>
        </div>
        <div className="space-y-1">
          <p className="font-medium">Available tools / endpoints</p>
          <ul className="list-inside list-disc space-y-0.5 text-muted-foreground">
            <li>list_employees — directory roster (HR-admin token)</li>
            <li>list_leave_requests — filter by status and date range</li>
            <li>list_expenses — filter by status or category</li>
            <li>list_invoices / list_bills — accounting AR/AP</li>
            <li>webhooks — manage outbound event subscriptions</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

function SettingsApiTokensRoute() {
  const q = useApiTokens();
  const revoke = useRevokeApiToken();
  const [pendingRevoke, setPendingRevoke] = useState<number | null>(null);
  const [revealed, setRevealed] = useState<{
    raw: string;
    name: string;
  } | null>(null);

  function handleRevoke(id: number) {
    setPendingRevoke(id);
    revoke.mutate(id, {
      onSuccess: () => {
        toast.success("Token revoked");
        setPendingRevoke(null);
      },
      onError: (err) => {
        toast.error(toApiError(err).message);
        setPendingRevoke(null);
      },
    });
  }

  const tokens = q.data?.items ?? [];
  const activeTokens = tokens.filter((t) => t.revoked_at == null);
  const revokedTokens = tokens.filter((t) => t.revoked_at != null);

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">API tokens</h2>
            <p className="text-sm text-muted-foreground">
              Personal bearer tokens for the WorkRight MCP server and the
              public API (<code>/api/v1/public/*</code>). Each token is scoped
              to your account and this workspace.
            </p>
          </div>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <KeyRound aria-hidden className="h-4 w-4 text-primary" />
                  Your tokens
                </CardTitle>
                <CardDescription>
                  Revoke tokens you no longer use. A revoked token stops
                  working immediately.
                </CardDescription>
              </div>
              <CreateTokenDialog
                onCreated={(raw, name) => setRevealed({ raw, name })}
              />
            </CardHeader>
            <CardContent>
              {q.isLoading && (
                <div className="space-y-2">
                  {["t1", "t2"].map((k) => (
                    <Skeleton key={k} className="h-16 w-full rounded-md" />
                  ))}
                </div>
              )}
              {q.isError && (
                <p className="text-sm text-destructive">
                  {toApiError(q.error).message}
                </p>
              )}
              {!q.isLoading && !q.isError && activeTokens.length === 0 && (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No active tokens. Create one to connect the MCP server or the
                  public API.
                </p>
              )}
              {!q.isLoading && !q.isError && activeTokens.length > 0 && (
                <div className="space-y-2" data-testid="api-tokens-list">
                  {activeTokens.map((token) => (
                    <TokenRow
                      key={token.id}
                      token={token}
                      onRevoke={handleRevoke}
                      revoking={pendingRevoke === token.id}
                    />
                  ))}
                </div>
              )}
              {revokedTokens.length > 0 && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                    {revokedTokens.length} revoked token
                    {revokedTokens.length !== 1 ? "s" : ""}
                  </summary>
                  <div className="mt-2 space-y-2 opacity-60">
                    {revokedTokens.map((token) => (
                      <TokenRow
                        key={token.id}
                        token={token}
                        onRevoke={handleRevoke}
                        revoking={false}
                      />
                    ))}
                  </div>
                </details>
              )}
            </CardContent>
          </Card>

          <MCPInstructions hasActiveToken={activeTokens.length > 0} />

          {revealed && (
            <RevealTokenDialog
              rawToken={revealed.raw}
              tokenName={revealed.name}
              onClose={() => setRevealed(null)}
            />
          )}
        </div>
      </SettingsShell>
    </>
  );
}
